import { useState, useEffect } from "react";
import { Box, Typography, Switch, FormControlLabel, CircularProgress, Alert } from "@mui/material";
import { enqueueSnackbar } from "notistack";

import DashboardHeader from "@/components/app/DashboardHeader";
import DashboardContent from "@/components/app/DashboardContent";
import { useGetCurrencyExchangeSettings, useUpdateCurrencyExchangeSettings } from "@/api/hooks/useSystemSettings";
import { getAxiosError } from "@/utils/get-axios-error";

const CurrencyExchange: React.FC = () => {
    const { data, isLoading, isError } = useGetCurrencyExchangeSettings();
    const { mutate: updateSettings, isPending } = useUpdateCurrencyExchangeSettings();
    const [enabled, setEnabled] = useState<boolean>(false);

    useEffect(() => {
        if (data?.data?.enabled !== undefined) {
            setEnabled(data.data.enabled);
        }
    }, [data]);

    const handleToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.checked;
        setEnabled(newValue);

        updateSettings(
            { enabled: newValue },
            {
                onSuccess: () => {
                    enqueueSnackbar(`Currency exchange ${newValue ? "enabled" : "disabled"} successfully`, {
                        variant: "success",
                    });
                },
                onError: (err) => {
                    // Revert on error
                    setEnabled(!newValue);
                    const error = getAxiosError(err);
                    const message = error?.meta.message || "Failed to update currency exchange settings";
                    enqueueSnackbar(message, { variant: "error" });
                },
            }
        );
    };

    if (isLoading) {
        return (
            <>
                <DashboardHeader title="Currency Exchange Settings">
                    <Typography component="p">Configure currency exchange settings for the application</Typography>
                </DashboardHeader>
                <DashboardContent>
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                        <CircularProgress />
                    </Box>
                </DashboardContent>
            </>
        );
    }

    if (isError) {
        return (
            <>
                <DashboardHeader title="Currency Exchange Settings">
                    <Typography component="p">Configure currency exchange settings for the application</Typography>
                </DashboardHeader>
                <DashboardContent>
                    <Alert severity="error">Failed to load currency exchange settings. Please try again later.</Alert>
                </DashboardContent>
            </>
        );
    }

    return (
        <>
            <DashboardHeader title="Currency Exchange Settings">
                <Typography component="p">Configure currency exchange settings for the application</Typography>
            </DashboardHeader>
            <DashboardContent>
                <Box sx={{ p: 3 }}>
                    <FormControlLabel
                        control={<Switch checked={enabled} onChange={handleToggle} disabled={isPending} color="primary" />}
                        label={
                            <Typography variant="body1" component="span">
                                Enable Currency Exchange
                            </Typography>
                        }
                    />
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="body2" color="text.secondary" paragraph>
                            {enabled
                                ? "Currency exchange is currently enabled. Prices will be converted to local currencies based on exchange rates."
                                : "Currency exchange is currently disabled. All prices will be displayed in USD only, with no currency conversion applied."}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            <strong>When enabled:</strong> Prices convert based on exchange rates. Website and apps display
                            local currency.
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            <strong>When disabled:</strong> All prices show in USD only. No currency conversion is applied.
                            Currency dropdowns and location-based detection are hidden.
                        </Typography>
                    </Box>
                </Box>
            </DashboardContent>
        </>
    );
};

export default CurrencyExchange;
